import { SelfContainedCommand, CommandExecutionContext } from '../../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('PLAN_CREATE');

/**
 * Default content blocks seeded into a new Plan entity.
 * Provides the three-layer structure: checklist, YAML state block, activity log.
 */
const PLAN_TEMPLATE_OPERATIONS = [
  {
    op: 'insert',
    target: { at: 'end' },
    content: {
      className: 'page-callout',
      attrs: {
        text: 'This plan tracks workflow progress. The YAML state block is machine-readable — RiverBot reads and updates it to resume reliably if interrupted.',
        icon: '📋',
        variant: 'info',
      },
    },
  },
  {
    op: 'insert',
    target: { at: 'end' },
    content: { className: 'page-heading-2', attrs: { text: 'Checklist' } },
  },
  {
    op: 'insert',
    target: { at: 'end' },
    content: { className: 'page-todo', attrs: { text: 'Step 1', checked: false } },
  },
  {
    op: 'insert',
    target: { at: 'end' },
    content: { className: 'page-todo', attrs: { text: 'Step 2', checked: false } },
  },
  {
    op: 'insert',
    target: { at: 'end' },
    content: { className: 'page-todo', attrs: { text: 'Step 3', checked: false } },
  },
  {
    op: 'insert',
    target: { at: 'end' },
    content: { className: 'page-heading-2', attrs: { text: 'Plan State' } },
  },
  {
    op: 'insert',
    target: { at: 'end' },
    content: {
      className: 'page-code',
      attrs: {
        language: 'yaml',
        text: [
          'workflow:',
          '  status: not_started  # not_started | in_progress | complete | archived',
          '  current_step: init',
          '  started_at: null',
          '  updated_at: null',
        ].join('\n'),
      },
    },
  },
  {
    op: 'insert',
    target: { at: 'end' },
    content: { className: 'page-heading-2', attrs: { text: 'Activity Log' } },
  },
  {
    op: 'insert',
    target: { at: 'end' },
    content: {
      className: 'page-paragraph',
      attrs: { text: '<!-- entries appended here by plan_append_log -->' },
    },
  },
];

export const planCreateCommand: SelfContainedCommand = {
  id: 'plan_create',
  description: '📋 CREATE: Create a new Plan — a workflow checkpoint document with machine-readable state',
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Display name for the Plan',
        required: false,
        defaultValue: 'New Plan',
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID (uses current space if not provided)',
        required: false,
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Folder to create the plan in',
        required: false,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { name: 'Chief of Staff Setup' },
        expectedOutput: createSuccessResult('Plan created', { entityId: 'plan_abc' }),
        description: 'Create a setup workflow plan',
      },
    ],
    bad: [],
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { name = 'New Plan', spaceId: providedSpaceId, folderId } = params;
    const spaceId = providedSpaceId || context.spaceId;

    try {
      log.info('[plan_create] Creating Plan:', { name, spaceId });

      const entityStoreModule = await import('../../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();

      const newEntity = await entityStore.actions.createEntity({
        entityType: 'Plan',
        name: name.trim() || 'New Plan',
        icon: { type: 'phosphor', phosphorIcon: 'ClipboardText', phosphorWeight: 'duotone' },
        spaceId,
        skeleton: {
          '@type': 'Plan',
          status: 'not_started',
          linkedSkillId: null,
          groupIds: folderId ? [folderId] : [],
        },
      } as any);

      const entityId = newEntity._id || newEntity.id;
      const entityLlmId = (newEntity as any).llmId;

      // Register llmId mapping
      if (entityLlmId && entityId) {
        try {
          const { llmIdMapper } = await import('../../../../utils/llmIdMapper');
          llmIdMapper.registerMapping(entityLlmId, entityId, 'document', 'Plan', newEntity.name);
        } catch { /* non-fatal */ }
      }

      // Seed the template content
      if (entityLlmId) {
        // Wait briefly for the Page application to be ready, then seed
        setTimeout(async () => {
          try {
            const { useCommandRegistryStore } = await import('../../../../stores/commandRegistryStore');
            await useCommandRegistryStore.getState().executeCommand('page_write', {
              entityId: entityLlmId,
              operation: 'batch',
              operations: PLAN_TEMPLATE_OPERATIONS,
            });
          } catch (err) {
            log.warn('[plan_create] Template seeding failed (non-fatal):', err);
          }
        }, 500);
      }

      return createSuccessResult(`Plan created: ${name}`, {
        entityId,
        entityLlmId,
        name: newEntity.name,
        entityType: 'Plan',
      });
    } catch (error: any) {
      log.error('[plan_create] Error:', error);
      return createErrorResult('Failed to create Plan', error.message || 'Unknown error', 'execution');
    }
  },
};

entityCreationRegistry.register({
  commandId: 'plan_create',
  entityType: 'Plan',
  displayName: 'Plan',
  icon: { type: 'phosphor', phosphorIcon: 'ClipboardText', phosphorWeight: 'duotone' },
  description: 'Workflow checkpoint document with machine-readable state',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'system',
  sortOrder: 115,
});
